import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { 
  CalculatorIcon, 
  ArrowLeftIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { closingsAPI } from '../../api';
import { useAppStore } from '../../stores/useAppStore';
import { CurrencyInput, Button } from '../ui';
import { formatDate } from '../../utils/dayjs';
import customDayjs from '../../utils/dayjs';

const ClosingForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addNotification } = useAppStore();
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [calculations, setCalculations] = useState({});
  const [previousClosing, setPreviousClosing] = useState(null);
  // true = an earlier closing exists and its ClosingAmount carries over as the
  // opening; false = none exists (first closing), so the manager types it in.
  const [hasPreviousClosing, setHasPreviousClosing] = useState(null);
  // Set once the manager types an opening, so a date change doesn't wipe it.
  const openingTypedRef = useRef(false);
  const [existingClosing, setExistingClosing] = useState(null);
  const [isExistingClosing, setIsExistingClosing] = useState(false);
  
  const isEdit = Boolean(id);
  const closingAmountRef = useRef(null);

  const { register, handleSubmit, watch, setValue, formState: { errors }, trigger } = useForm({
    defaultValues: {
      ClosingDate: new Date().toISOString().split('T')[0],
      OpeningAmount: 0,
      CashSales: 0,
      Refunds: 0,
      Expenses: 0,
      ClosingAmount: '',
      BankReceived: 0,
      BankRefunds: 0,
      Notes: ''
    },
    mode: 'onChange'
  });

  // Watch all form values for real-time updates
  const formValues = watch();

  // Load existing closing for edit
  useEffect(() => {
    if (isEdit) {
      loadClosing();
    } else {
      // For new closing, get previous day's closing amount
      loadPreviousClosing();
    }
  }, [isEdit]);

  // Auto-calculate when date changes
  useEffect(() => {
    if (formValues.ClosingDate && !isEdit) {
      // First check if closing already exists for this date
      checkExistingClosing(formValues.ClosingDate);
      calculateClosing(formValues.ClosingDate);
      // Also load previous closing for the new date
      loadPreviousClosingForDate(formValues.ClosingDate);
    }
  }, [formValues.ClosingDate, isEdit]);

  const loadClosing = async () => {
    try {
      setLoading(true);
      const response = await closingsAPI.get(id);
      const closing = response.data;
      
      // Set form values
      Object.keys(closing).forEach(key => {
        if (closing[key] !== null && closing[key] !== undefined) {
          setValue(key, closing[key]);
        }
      });
    } catch (error) {
      console.error('Error loading closing:', error);
      addNotification({
        type: 'error',
        message: 'Failed to load closing details'
      });
      navigate('/closings');
    } finally {
      setLoading(false);
    }
  };

  const loadPreviousClosing = () => {
    // Pass today's date; backend returns the most recent closing strictly before it,
    // so holiday/weekend gaps still resolve to the actual last closing.
    const todayStr = new Date().toISOString().split('T')[0];
    return loadPreviousClosingForDate(todayStr);
  };

  const checkExistingClosing = async (selectedDate) => {
    try {
      console.log('Checking for existing closing on date:', selectedDate);
      
      // Get list of closings for this date
      const response = await closingsAPI.list({
        start_date: selectedDate,
        end_date: selectedDate,
        limit: 1
      });
      
      if (response.data && response.data.length > 0) {
        const existing = response.data[0];
        console.log('Found existing closing:', existing);
        setExistingClosing(existing);
        setIsExistingClosing(true);
        
        // Populate form with existing closing data
        Object.keys(existing).forEach(key => {
          if (existing[key] !== null && existing[key] !== undefined) {
            setValue(key, existing[key]);
          }
        });
        
        addNotification({
          type: 'warning',
          message: `Closing already exists for ${selectedDate}. Form is now in view-only mode.`
        });
      } else {
        console.log('No existing closing found for date:', selectedDate);
        setExistingClosing(null);
        setIsExistingClosing(false);
      }
    } catch (error) {
      console.log('Error checking for existing closing:', error);
      setExistingClosing(null);
      setIsExistingClosing(false);
    }
  };

  const loadPreviousClosingForDate = async (selectedDate) => {
    try {
      // Backend returns the most recent closing strictly before selectedDate,
      // so any gap (holiday, weekend, missed day) still resolves correctly.
      const response = await closingsAPI.getPrevious(selectedDate);
      if (response.data) {
        // Carry over even a 0 closing, so a typed opening can't linger.
        const carried = Number(response.data.ClosingAmount) || 0;
        setValue('OpeningAmount', carried);
        setPreviousClosing(carried);
        setHasPreviousClosing(true);
        openingTypedRef.current = false;
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.log('Error loading previous closing for date:', error);
      } else {
        // No earlier closing at all: the opening is whatever cash is already
        // in the drawer, so let the manager enter it.
        if (!openingTypedRef.current) setValue('OpeningAmount', 0);
        setPreviousClosing(0);
        setHasPreviousClosing(false);
      }
    }
  };

  const calculateClosing = async (date) => {
    try {
      setCalculating(true);
      console.log('Calculating closing for date:', date);
      
      // Get user's timezone
      const userTimezone = customDayjs.tz.guess();
      console.log('User timezone:', userTimezone);
      
      const response = await closingsAPI.calculate({ date, timezone: userTimezone });
      const calc = response.data;
      
      console.log('Calculated values:', calc);
      setCalculations(calc);
      
      // Update form with calculated values
      setValue('CashSales', calc.CashSales || 0);
      setValue('Refunds', calc.Refunds || 0);
      setValue('BankReceived', calc.BankReceived || 0);
      setValue('BankRefunds', calc.BankRefunds || 0);
      setValue('Expenses', calc.Expenses || 0);

    } catch (error) {
      console.error('Error calculating closing:', error);
      addNotification({
        type: 'error',
        message: 'Failed to calculate closing values'
      });
    } finally {
      setCalculating(false);
    }
  };

  const handleRecalculate = () => {
    if (formValues.ClosingDate) {
      calculateClosing(formValues.ClosingDate);
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      
      // Convert string values to numbers for required fields with proper precision handling
      const submitData = {
        ...data,
        Expenses: parseFloat(data.Expenses) || 0,
        ClosingAmount: data.ClosingAmount ? parseFloat(data.ClosingAmount) : 0,
        OpeningAmount: parseFloat(data.OpeningAmount) || 0,
        CashSales: parseFloat(data.CashSales) || 0,
        Refunds: parseFloat(data.Refunds) || 0,
        BankReceived: parseFloat(data.BankReceived) || 0,
        BankRefunds: parseFloat(data.BankRefunds) || 0
      };
      
      if (isEdit) {
        await closingsAPI.update(id, submitData);
        addNotification({
          type: 'success',
          message: 'Closing updated successfully'
        });
      } else {
        await closingsAPI.create(submitData);
        addNotification({
          type: 'success',
          message: 'Closing created successfully'
        });
      }
      
      navigate('/closings');
    } catch (error) {
      console.error('Error saving closing:', error);
      addNotification({
        type: 'error',
        message: error.response?.data?.detail || 'Failed to save closing'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    // Don't hijack Enter inside multi-line inputs (e.g. the Notes textarea) —
    // let it insert a newline. Enter still submits from single-line fields.
    const isTextarea = event.target.tagName === 'TEXTAREA';
    if (event.key === 'Enter' && !event.ctrlKey && !isTextarea) {
      event.preventDefault();
      handleSubmit(onSubmit)();
    } else if (event.key === 'Escape') {
      navigate('/closings');
    }
  };

  // Only the very first closing has no carried-over opening to use.
  const canEditOpening = !isEdit && !isExistingClosing && hasPreviousClosing === false;

  // Calculate total for financial summary
  const calculateTotal = () => {
    const opening = parseFloat(formValues.OpeningAmount) || 0;
    const cashSales = parseFloat(formValues.CashSales) || 0;
    const refunds = parseFloat(formValues.Refunds) || 0;
    const expenses = parseFloat(formValues.Expenses) || 0;
    const closingAmount = parseFloat(formValues.ClosingAmount) || 0;
    
    return opening + cashSales - refunds - expenses - closingAmount;
  };

  if (loading && isEdit) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="mt-2 text-sm text-gray-500">Loading closing...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/closings')}
            className="inline-flex items-center p-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ArrowLeftIcon className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEdit ? 'Edit Closing' : 'New Daily Closing'}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              {isEdit ? 'Update closing details' : 'Create a new daily closing with automatic calculations'}
            </p>
          </div>
        </div>
      </div>

      {/* Existing Closing Alert */}
      {isExistingClosing && existingClosing && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Existing Closing Found
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>A closing already exists for {formatDate(existingClosing.ClosingDate)}. You can view the details or edit it from the closings list.</p>
                <div className="mt-2">
                  <button
                    onClick={() => navigate(`/closings/${existingClosing.ID}`)}
                    className="text-yellow-800 hover:text-yellow-900 font-medium underline"
                  >
                    View Existing Closing →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} onKeyDown={handleKeyDown} className="space-y-6">
        {/* Date Selection */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="ClosingDate" className="block text-sm font-medium text-gray-700">
                Closing Date *
              </label>
              <input
                {...register('ClosingDate', { required: 'Closing date is required' })}
                type="date"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                disabled={isEdit}
              />
              {errors.ClosingDate && (
                <p className="mt-1 text-sm text-red-600">{errors.ClosingDate.message}</p>
              )}
            </div>
            
            <div className="flex items-end">
              <Button
                type="button"
                onClick={handleRecalculate}
                disabled={calculating || !formValues.ClosingDate}
              >
                {calculating ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <CalculatorIcon className="h-4 w-4 mr-2" />
                )}
                {calculating ? 'Calculating...' : 'Recalculate'}
              </Button>
            </div>
          </div>
        </div>

        {/* Two-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Cash Operations</h3>
              
              <div className="space-y-4">
                {/* Opening Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Opening Amount</label>
                  <CurrencyInput
                    value={parseFloat(formValues.OpeningAmount) || 0}
                    onChange={canEditOpening ? (val) => {
                      openingTypedRef.current = true;
                      setValue('OpeningAmount', val);
                    } : undefined}
                    readOnly={!canEditOpening}
                    disabled={isExistingClosing}
                    showZero={!canEditOpening}
                    min={0}
                    placeholder="0.00"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                  {hasPreviousClosing !== null && !isExistingClosing && (
                    <p className="mt-1 text-xs text-gray-500">
                      {hasPreviousClosing
                        ? `Carried over from the last closing: RS ${Number(previousClosing).toFixed(2)}`
                        : 'No previous closing found - enter the cash already in the drawer'
                      }
                    </p>
                  )}
                </div>

                {/* Cash Sales */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Cash Sales</label>
                  <CurrencyInput
                    value={parseFloat(formValues.CashSales) || 0}
                    readOnly
                    disabled={isExistingClosing}
                    showZero
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Auto-calculated from sales on {formatDate(formValues.ClosingDate)}
                  </p>
                </div>

                {/* Refunds */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Refunds</label>
                  <CurrencyInput
                    value={parseFloat(formValues.Refunds) || 0}
                    readOnly
                    disabled={isExistingClosing}
                    showZero
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Auto-calculated from returns and exchanges
                  </p>
                </div>

                {/* Expenses */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Expenses <span className="text-xs text-gray-400">(auto-calculated)</span>
                  </label>
                  <CurrencyInput
                    value={parseFloat(formValues.Expenses) || 0}
                    readOnly
                    disabled={true}
                    showZero
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Auto-calculated from expense records
                  </p>
                </div>

                {/* Closing Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Closing Amount *</label>
                  <CurrencyInput
                    ref={closingAmountRef}
                    value={parseFloat(formValues.ClosingAmount) || 0}
                    onChange={(val) => {
                      setValue('ClosingAmount', val);
                      trigger('ClosingAmount');
                    }}
                    min={0}
                    placeholder="0.00"
                    disabled={isExistingClosing}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                  {errors.ClosingAmount && (
                    <p className="mt-1 text-sm text-red-600">{errors.ClosingAmount.message}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">Amount left in drawer for next day</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Bank Operations</h3>
              
              <div className="space-y-4">
                {/* Bank Received */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Bank Received</label>
                  <CurrencyInput
                    value={parseFloat(formValues.BankReceived) || 0}
                    readOnly
                    disabled={isExistingClosing}
                    showZero
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Auto-calculated from sales and exchanges
                  </p>
                </div>

                {/* Bank Refunds — recorded only; never part of the drawer math. */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Bank Refunds</label>
                  <CurrencyInput
                    value={parseFloat(formValues.BankRefunds) || 0}
                    readOnly
                    disabled={isExistingClosing}
                    showZero
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Auto-calculated from returns refunded by bank — not deducted from cash
                  </p>
                </div>
              </div>
            </div>

            {/* Financial Summary */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Financial Summary</h3>
              
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Opening Amount:</span>
                  <span className="text-sm font-medium">RS {(parseFloat(formValues.OpeningAmount) || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Cash Sales:</span>
                  <span className="text-sm font-medium text-green-600">+RS {(parseFloat(formValues.CashSales) || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Refunds:</span>
                  <span className="text-sm font-medium text-red-600">-RS {(parseFloat(formValues.Refunds) || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Expenses:</span>
                  <span className="text-sm font-medium text-red-600">-RS {(parseFloat(formValues.Expenses) || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Closing Amount:</span>
                  <span className="text-sm font-medium text-red-600">-RS {(parseFloat(formValues.ClosingAmount) || 0).toFixed(2)}</span>
                </div>
                <hr className="my-2" />
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-900">Total:</span>
                  <span className="text-sm font-medium">
                    RS {calculateTotal().toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white p-6 rounded-lg shadow">
          <label htmlFor="Notes" className="block text-sm font-medium text-gray-700">
            Notes
          </label>
          <textarea
            {...register('Notes')}
            rows={3}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            placeholder="Additional notes about the closing..."
            disabled={isExistingClosing}
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <Button variant="secondary" onClick={() => navigate('/closings')}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading || isExistingClosing}>
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <CheckIcon className="h-4 w-4 mr-2" />
            )}
            {loading ? 'Saving...' : (isEdit ? 'Update Closing' : 'Create Closing')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ClosingForm;